import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import CustomizedSwitch from '../../../../components/Switch';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import ProductService from '../../../../services/product.service';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';

import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';

interface ProductRow {
  id: number;
  product_code: string;
  product_name: string;
  status_id: number | null;
  status_name: string | null;
  status_colour: string | null;
  display_order: number;
}

const ProductList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'product_code' });
  const [items, setItems] = useState<ProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductRow | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const { loading } = useFetch({
    service: ProductService,
    func: 'list',
    payload: { includeDeleted: showInactive, page, limit: perPage },
    dependency: [showInactive, page, perPage, refreshToken],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.masters}${AuthRoutes.productMaster}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const response: any = await ProductService.remove(deleteTarget.id);
      const softOrHard = response?.data?.softOrHard;
      toast.success(
        <Toast
          message={
            softOrHard === 'soft'
              ? t('masters.products.list.pageTexts.deleteSuccessSoft')
              : t('masters.products.list.pageTexts.deleteSuccessHard')
          }
        />,
        { icon: false }
      );
      setRefreshToken((v) => v + 1);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'product_code', sortEnabled: true, width: '20%', label: t('masters.products.list.pageTexts.table.columns.code') },
      { id: 'product_name', sortEnabled: true, width: '30%', label: t('masters.products.list.pageTexts.table.columns.name') },
      { id: 'status', sortEnabled: true, width: '20%', label: t('masters.products.list.pageTexts.table.columns.status') },
      { id: 'display_order', sortEnabled: true, width: '15%', label: t('masters.products.list.pageTexts.table.columns.displayOrder') },
      { id: 'actions', sortEnabled: false, width: '15%', label: t('masters.products.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: ProductRow; className?: string }) => (
      <tr className={className}>
        <td>{item.product_code}</td>
        <td>{item.product_name}</td>
        <td>
          {item.status_name && item.status_colour ? (
            <StatusBadge label={item.status_name} colour={item.status_colour} />
          ) : (
            '—'
          )}
        </td>
        <td>{item.display_order}</td>
        <td className="text-end">
          <RowActions
            actions={[
              {
                id: 'edit',
                label: t('common.edit') || 'Edit',
                icon: <i className="ri-pencil-line" />,
                onClick: () => handleEdit(item.id),
              },
              {
                id: 'delete',
                label: t('common.delete') || 'Delete',
                icon: <i className="ri-delete-bin-line" />,
                danger: true,
                onClick: () => setDeleteTarget(item),
              },
            ]}
          />
        </td>
      </tr>
    ),
    [handleEdit]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('masters.products.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('masters.products.list.pageTexts.title')}</h4>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('masters.products.list.pageTexts.showInactive')}
            name="showInactive"
            checked={showInactive}
            onChange={() => setShowInactive((v) => !v)}
          />
          <Button
            className="btn btn-primary"
            text={t('masters.products.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.productMaster}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('masters.products.list.pageTexts.deleteDialog.title')}
        content={t('masters.products.list.pageTexts.deleteDialog.body')}
        action={
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('masters.products.list.pageTexts.deleteDialog.cancel')}
              onClick={() => setDeleteTarget(null)}
            />
            <Button
              className="btn btn-danger"
              text={t('masters.products.list.pageTexts.deleteDialog.confirm')}
              onClick={handleDeleteConfirm}
            />
          </>
        }
      />
    </>
  );
};

export default ProductList;
